import { useCallback, useState } from 'react'
import { CircularProgress, Typography } from '@mui/material'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'

import { RequestParams } from '@/Components/Pages/Login/types'

import { headers } from '@/util/requests'

import useCsrfToken from '@/hooks/useCsrfToken'
import { Row } from '../../Layout/Row'

import TextField from '@/Components/Common/Inputs/TextField'
import Card from '@/Components/Pages/Login/Card'
import CardButton from '@/Components/Pages/Login/CardButton'
import CardHeader from '@/Components/Pages/Login/CardHeader'

type LoginCardProps = {
  changePassword: () => void
}

const login = async ({ csrfToken, user }: RequestParams) => {
  const response = await fetch('/users/sign_in', {
    method: 'POST',
    headers: headers(csrfToken),
    body: JSON.stringify({ user })
  })

  if (!response.ok) {
    return { loggedIn: false, error: 'Login failed', redirectUrl: null }
  }

  const { error, redirectUrl } = await response.json()

  return { loggedIn: !error, error, redirectUrl }
}

const LoginCard: React.FC<LoginCardProps> = ({ changePassword }) => {
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false)
  const [hasEmail, setHasEmail] = useState<boolean>(false)
  const [hasPassword, setHasPassword] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const csrfToken = useCsrfToken()

  const handleInputChange = useCallback(() => {
    setError(null)
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const formData = new FormData(e.currentTarget)
      const email = formData.get('email') as string
      const password = formData.get('password') as string

      if (!email || !password) {
        setError('Email and password are required')
        return
      }

      setLoading(true)

      const {
        loggedIn,
        error = null,
        redirectUrl
      } = await login({
        csrfToken,
        user: { email, password }
      })

      setLoading(false)
      setError(error)

      if (loggedIn) {
        setNextPageLoading(true)
        window.location.href = redirectUrl || '/'
        return
      }
    },
    []
  )

  return (
    <Card error={error}>
      <Stack gap={1.5} component="form" onSubmit={handleSubmit}>
        <CardHeader title="Login" error={error} />
        {nextPageLoading && (
          <Stack minHeight={350} justifyContent="center" alignItems="center">
            <CircularProgress />
            <Typography>Loading Assessments...</Typography>
          </Stack>
        )}
        {!nextPageLoading && (
          <Stack gap={5}>
            <Stack gap={4}>
              <Stack gap={1}>
                <TextField
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  error={!!error}
                  onChange={(e) => {
                    setHasEmail(e.target.value.length > 0)
                    handleInputChange()
                  }}
                />
                <TextField
                  id="password"
                  label="Password"
                  name="password"
                  type="password"
                  error={!!error}
                  autoComplete="current-password"
                  onChange={(e) => {
                    setHasPassword(e.target.value.length > 0)
                    handleInputChange()
                  }}
                />
              </Stack>
              <CardButton
                loading={loading}
                disabled={!hasEmail || !hasPassword}
              >
                Log in
              </CardButton>
            </Stack>
            <Row justifyContent="space-between">
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  changePassword()
                }}
              >
                Forgot password?
              </Link>
              <Link href="mailto:support@example.com">Contact support</Link>
            </Row>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
export default LoginCard
